import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { finalize, take } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger } from '@core';
import {
  AuthenticationService,
  LoginResponse,
  UserPhoneNumber,
} from './authentication.service';
import { fade, slide, swipe } from '@app/animations';
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { I18nService } from '@app/i18n';
import { LanguageService } from '@app/@core/services/language.service';
import { CredentialsService } from '@core/services/credentials.service';
import { ContactInfo } from '@app/contact/contact.types';
import { ContactService } from '@app/contact/contact.service';
import { MatDialog } from '@angular/material/dialog';
import { ForceResetPasswordDialogComponent } from './force-reset-password-dialog/force-reset-password-dialog.component';

const log = new Logger('Login');

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [fade, slide, swipe],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  version: string | null = environment.version;

  error: string | undefined;

  isAccountDeactivated = false;

  loginForm!: FormGroup;
  otpForm!: FormGroup;
  verifyOtpForm!: FormGroup;

  isLoading = false;
  phones: UserPhoneNumber[] = [];
  credentials: Partial<LoginResponse> = {};

  currentLanguage: string;

  state = 1;

  country = {
    name: environment.countrySettings.country,
    code: environment.countrySettings.countryCode,
  };

  htmlTag: HTMLHtmlElement;
  dir: string;
  showPassword: boolean = false;
  contactInfo: ContactInfo;
  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private i18nService: I18nService,
    private languageService: LanguageService,
    private credentialsService: CredentialsService,
    private authenticationService: AuthenticationService,
    private contactService: ContactService,
    private dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.changeLanguageButtonText();
    // forcing the (rtl || ltr) direction in login component
    this.forceUpdateHtmlDirection();
    this.getSupportContact();

    this.loginForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isAccountDeactivated) {
          this.isAccountDeactivated = false;
        }
        this.error = undefined;
      });
  }

  ngOnDestroy() {}
  getSupportContact() {
    this.contactService
      .getContactsInfo()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ContactInfo) => {
          this.contactInfo = res;
        },
        error: (err) => {
          log.debug(err);
        },
      });
  }
  private isDeactivatedError(err: HttpErrorResponse): boolean {
    const code = err?.error?.errors?.[0];
    if (code === 'AccountDeactivated') return true;

    const msg: string = (err?.error?.message || err?.message || '').toString();
    if (!msg) return false;

    return msg.toLowerCase().includes('deactivated');
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const codes: string[] | undefined = err?.error?.errors;
    if (codes?.length) return codes.join(', ');

    const msg: string | undefined = err?.error?.message;
    if (msg) return msg;

    if (err instanceof HttpErrorResponse) {
      return err.message || 'Login failed';
    }

    return 'Login failed';
  }

  login() {
    // if the user using phone number
    // and there is no + infront of the number -> add it
    const phone = this.loginForm.get('username')?.value;
    if (phone * 1 && phone[0] !== '+') {
      this.loginForm.get('username')?.setValue(`+${phone}`);
    }

    this.isLoading = true;

    this.authenticationService
      .login(this.loginForm.value)
      .pipe(
        finalize(() => {
          this.loginForm.markAsPristine();
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (credentials) => {
          this.isAccountDeactivated = false;
          this.error = undefined;

          this.credentials = credentials;
          this.completeLogin(credentials);
        },
        error: (err) => {
          if (this.isDeactivatedError(err)) {
            this.isAccountDeactivated = true;
            this.error = undefined;
            return;
          }

          this.isAccountDeactivated = false;
          this.error = this.extractErrorMessage(err);
        },
      });
  }

  dismissDeactivatedBanner() {
    this.isAccountDeactivated = false;
  }

  private showOTP(credentials: Partial<LoginResponse>) {
    this.state = 2;
    if (credentials.access_token) {
      this.isLoading = true;
      this.authenticationService
        .getUserAllPhoneNumbers(credentials.access_token)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (res) => {
            this.phones = res;
            // set a defaule value for the phone number as the first one on the response
            if (res && res.length > 0) {
              this.otpForm.get('phone').setValue(res[0].phoneNumber);
            }
          },
          error: (err) => {
            log.debug(err);
          },
        });
    }
  }

  sendOtp() {
    let phone = this.otpForm.get('phone').value;
    if (this.credentials.access_token) {
      this.isLoading = true;
      this.authenticationService
        .sendOtp(this.credentials.access_token, phone)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (res) => {
            // set the state to verify otp
            this.state = 3;
          },
          error: (err) => {},
        });
    }
  }

  verifyOTP() {
    let code = this.verifyOtpForm.get('code').value;
    let phone = this.otpForm.get('phone').value;

    if (this.credentials.access_token) {
      this.isLoading = true;
      this.authenticationService
        .verifyOtp(this.credentials.access_token, code, phone)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (res) => {
            if (res.isValid) {
              this.completeLogin(this.credentials);
            }
          },
          error: (err) => {
            this.error = 'OTP is invalid';
          },
        });
    }
  }

  resendOtp() {
    this.state = 2;
    this.error = null;
    this.verifyOtpForm.get('code').setValue('');
  }

  private saveCredentials(credentials: Partial<LoginResponse>) {
    const data = {
      username: this.loginForm.get('username').value,
      token: credentials.access_token,
      companyId: '0',
      refreshToken: credentials.refresh_token,
      companyName: credentials.companyName,
    };
    this.credentialsService.setCredentials(data, true);
  }

  private loadCompanyInformation() {
    this.isLoading = true;
    this.authenticationService
      .getCompanyDetails()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          if (res) {
            // company exist
            if (res.length > 0) {
              const _credentials = this.credentialsService.credentials;
              _credentials.companyId = res[0].id;
              _credentials.companyName = res[0].companyName;
              this.credentialsService.setCredentials(_credentials);
              this.router.navigate(
                [this.route.snapshot.queryParams.redirect || '/'],
                { replaceUrl: true },
              );
            }
            // no company
            if (res.length === 0) {
              const _credentials = this.credentialsService.credentials;
              this.credentialsService.setCredentials(_credentials);
              this.router.navigateByUrl('/createCompany');
            }
          }
        },
        error: (err) => {
          log.debug(err);
        },
      });
  }

  private completeLogin(credentials: Partial<LoginResponse>) {
    if (credentials.is_password_reset_required) {
      this.dialog.open(ForceResetPasswordDialogComponent, {
        width: '460px',
        disableClose: true,
        data: { email: this.loginForm.get('username').value },
      });
      return;
    }

    this.saveCredentials(credentials);

    this.router
      .navigate([this.route.snapshot.queryParams.redirect || '/'], {
        replaceUrl: true,
      })
      .then(() => window.location.reload());

    // this.loadCompanyInformation();
  }

  private createForm() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      remember: true,
    });
    this.otpForm = this.formBuilder.group({
      phone: ['', Validators.required],
    });
    this.verifyOtpForm = this.formBuilder.group({
      code: ['', Validators.required],
    });
  }

  changeLanguageButtonText() {
    if (
      localStorage.getItem('language') &&
      localStorage.getItem('language') === 'ar'
    ) {
      this.currentLanguage = localStorage.getItem('language');
    }
  }

  changeLanguage(currentLanguage: string) {
    let targetedLanguage = currentLanguage;
    this.i18nService.language = targetedLanguage;
    this.languageService.language = targetedLanguage;
    this.languageService.changeHtmlDirection(targetedLanguage);
    location.reload();
  }

  forceUpdateHtmlDirection() {
    this.htmlTag = this.document.getElementsByTagName(
      'html',
    )[0] as HTMLHtmlElement;
    this.htmlTag.dir = localStorage.getItem('html-dir');
  }

  showHidePassword() {
    this.showPassword = !this.showPassword;
  }
}
